import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { lighten } from '@mui/material/styles';
import classNames from 'classnames';
import SelectField from 'components/SelectField';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { withStyles } from 'tss-react/mui';

const styles = (theme) => ({
  root: {
    paddingRight: theme.spacing(1),
    minHeight: 30,
  },
  highlight:
    theme.palette.mode === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.secondary.dark,
        },
  spacer: {
    flex: '1 1 100%',
  },
  actions: {
    color: theme.palette.text.primary,
  },
  title: {
    flex: '0 0 auto',
  },
  formContainer: {
    display: 'flex',
    padding: '10px 24px 10px 24px',
    background: theme.palette.background.paper,
    flexDirection: 'row',
    flexFlow: 'wrap',
  },
  formLabel: {
    alignItems: 'flex-start',
  },
  textField: {
    marginRight: 15,
    marginBottom: 15,
    display: 'inline-block',
    flexDirection: 'column',
    color: theme.palette.text.secondary,
    minWidth: 120,
    '& input': {
      fontSize: theme.palette.register.fontSize.default,
      lineHeight: theme.palette.register.fontSize.default,
    },
  },
  button: {
    verticalAlign: 'bottom',
  },
});

class QListDetails extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      data: { ...props.data },
    };
  }

  componentDidMount() {
    setTimeout(() => {
      if (this.focusRef && this.focusField) {
        this.focusField.focus();
        this.focusField.select();
      }
    }, 50);
  }

  onKeyDown = (e, isNumber, filter) => {
    // console.log(e.key);
    switch (e.key) {
      case 'Tab':
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowRight':
      case 'ArrowLeft':
      case 'Backspace':
      case 'Delete':
        break;

      case 'Enter':
        this.saveData();
        if (this.props.onEscape) {
          this.props.onEscape();
        }
        break;

      case 'Escape':
        if (this.props.onEscape) {
          this.props.onEscape();
        }
        break;

      default:
        if (filter && filter.indexOf(e.key) >= 0) {
          e.stopPropagation();
          e.preventDefault();
        }
        if (isNumber) {
          if ('012345678990+-.'.indexOf(e.key) === -1) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
        break;
    }
  };

  saveData = () => {
    if (this.props.onSave) {
      // convert numeric fields to numbers as requested
      this.props.dataDictionary.forEach((col) => {
        if (this.isNumber(col.type)) {
          this.state.data[col.id] = Number(this.state.data[col.id]);
        }
      });
      this.props.onSave(this.state.data);
    }
  };

  isNumber = (type) => type === 'number';

  focusRef = (x) => {
    this.focusField = x;
  };

  handleChange = (val, id) => {
    const newData = { ...this.state.data };
    newData[id] = val;
    // console.log('CHANGE', id, val);
    this.setState({ data: newData });
  };

  handleCustomChange = (val, id) => {
    const newData = { ...this.state.data };
    newData.values[id] = val;
    // console.log('CHANGEC', id, val);
    this.setState({ data: newData });
  };

  render() {
    const { classes, dataDictionary, className } = this.props;
    const { data } = this.state;

    let takeFocus = true;

    return (
      <Paper>
        <form className={classNames(className, classes.formContainer)} noValidate autoComplete='one-time-code'>
          {dataDictionary.map((col) => {
            const EditComponent = col.component || TextField;
            const customProps = col.inputProps || {};
            let ret;

            /* eslint-disable jsx-a11y/no-static-element-interactions */
            if (col.choices && col.editable) {
              const children = col.choices.map((x) => {
                const obj = { name: col.type === 'icon' ? col.stringToIcon(x, true) : x, value: x };
                return obj;
              });
              ret = (
                <div
                  onKeyDown={(e) => this.onKeyDown(e, this.isNumber(col.type))}
                  key={`qlist_details_edit:${col.id}`}
                  className={classes.textField}
                >
                  <FormControlLabel
                    classes={{ labelPlacementTop: classes.formLabel }}
                    control={
                      <SelectField
                        value={data[col.id]}
                        name={col.type === 'icon' ? col.stringToIcon(data[col.id]) : ''}
                        onKeyDown={(e) => this.onKeyDown(e, this.isNumber(col.type))}
                        onChange={(e) => this.handleChange(e, col.id)}
                        idPrefix={`create-new-${col.label.toLowerCase()}`}
                      >
                        {children}
                      </SelectField>
                    }
                    label={col.label}
                    labelPlacement='top'
                  />
                </div>
              );
            } else {
              const useCustomValue = data.values && data.values[col.id];
              /* eslint-disable react/jsx-no-duplicate-props */
              ret = (
                <div
                  onKeyDown={(e) => this.onKeyDown(e, this.isNumber(col.type), col.filter)}
                  key={`qlist_details_edit:${col.id}`}
                  className={classes.textField}
                  id={`qlist-details-edit-${col.id}`}
                >
                  <FormControlLabel
                    classes={{ labelPlacementTop: classes.formLabel }}
                    control={
                      <EditComponent
                        disabled={!col.editable}
                        value={useCustomValue ? ' ' : data[col.id] || ''}
                        onChange={
                          !useCustomValue
                            ? (e) => this.handleChange(e.target.value, col.id)
                            : (e) => this.handleCustomChange(e, col.id)
                        }
                        InputProps={{
                          'aria-label': col.label,
                          inputComponent: col.inputComponent,
                        }}
                        inputProps={
                          useCustomValue
                            ? {
                                ...customProps,
                                'aria-label': col.id,
                                value: data.values[col.id] || '',
                                id: `create-new-${col.id.toLowerCase()}`,
                                maxLength: col.maxLength,
                              }
                            : {
                                ...customProps,
                                'aria-label': col.id,
                                ref: takeFocus ? this.focusRef : null,
                                id: `create-new-${col.id.toLowerCase()}`,
                                maxLength: col.maxLength,
                              }
                        }
                      />
                    }
                    label={col.label}
                    labelPlacement='top'
                  />
                </div>
              );
            }
            takeFocus = false;
            return ret;
          })}

          <Button
            color='primary'
            className={classes.button}
            onClick={this.props.onEscape}
            id='create-new-cancel-button'
          >
            Cancel
          </Button>
          <Button color='primary' className={classes.button} onClick={this.saveData} id='create-new-save-button'>
            Save
          </Button>
        </form>
      </Paper>
    );
  }
}

QListDetails.propTypes = {
  classes: PropTypes.object.isRequired,
  dataDictionary: PropTypes.array.isRequired,
  data: PropTypes.object.isRequired,
  className: PropTypes.string,
  onEscape: PropTypes.func,
  onSave: PropTypes.func,
};

export default withStyles(QListDetails, styles);
